import React, { useState } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'

import s from './styles.module.scss'

import { LogTypeEnum, Payment_Type } from '../../../../../../lib/graphql'
import { multiplyByFactor } from '../../../../../../utils/prices'
import { Button, BUTTON_COLOR, IconSvg } from '../../../../../atoms'
import { useLogsContext, useUserTypeContext } from '../../../../../contexts'
import { Modal } from '../../../../../molecules'

// Max amount for stripe is 10 000Euros
const MAX_AMOUNT_FOR_STRIPE = multiplyByFactor(10000)

const getPaymentTypeLabel = ({
  paymentType,
  intl,
}: {
  paymentType: Payment_Type
  intl: IntlShape
}) => {
  switch (paymentType) {
    case Payment_Type.DEPOSIT:
      return intl.formatMessage({
        id: 'ui.getPaymentTypeLabel.deposit',
        defaultMessage: 'l’acompte',
      })
    case Payment_Type.BALANCE:
      return intl.formatMessage({
        id: 'ui.getPaymentTypeLabel.balance',
        defaultMessage: 'le solde',
      })
    case Payment_Type.BALANCE_POST_FINAL:
      return intl.formatMessage({
        id: 'ui.getPaymentTypeLabel.balancePostFinal',
        defaultMessage: 'le solde après séjour',
      })
    default:
      return ''
  }
}

interface IPaymentModeModal {
  paymentAmount: number
  isPaymentModeModalOpen: boolean
  setIsPaymentModeModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  setIsLoadingPayment?: React.Dispatch<React.SetStateAction<boolean>>
  handlePayment?: () => void
  paymentType: Payment_Type
  clientRequestId: string
  houseId?: string | null
  serviceProviderId?: string | null
}

const PaymentModeModal = ({
  paymentAmount,
  isPaymentModeModalOpen,
  setIsPaymentModeModalOpen,
  paymentType,
  clientRequestId,
  handlePayment,
  houseId,
  serviceProviderId,
}: IPaymentModeModal) => {
  const intl = useIntl()
  const { isAdmin } = useUserTypeContext()
  const { createUserLog } = useLogsContext()

  const [isCardPayment, setIsCardPayment] = useState<boolean>(false)

  const showStripeButtons = !isAdmin && paymentAmount < MAX_AMOUNT_FOR_STRIPE

  return (
    <Modal
      isOpen={isPaymentModeModalOpen}
      title={intl.formatMessage({
        id: 'ui.PaymentModeModal.title',
        defaultMessage: 'Choisir le mode de paiement',
      })}
      scrollbarStyle={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}
      closeModal={() => setIsPaymentModeModalOpen(false)}
      className={s.paymentModeModal}
    >
      <FormattedMessage
        id='ui.PaymentModeModal.howToPay'
        defaultMessage='Comment souhaitez-vous payer {paymentType} de la réservation ?'
        values={{ paymentType: getPaymentTypeLabel({ paymentType, intl }) }}
      />
      <div className={s.paymentModeButtonsContainer}>
        {showStripeButtons && handlePayment && (
          <Button
            onClick={() => {
              setIsCardPayment(true)
              handlePayment()
            }}
            className={s.paymentModeButton}
            color={BUTTON_COLOR.WHITE}
            loading={isCardPayment}
            disabled={isCardPayment}
            fullWidth
            data-testid='pay-by-card-button'
          >
            <IconSvg.CardPayment
              style={{ alignSelf: 'center', marginRight: '20px' }}
            />
            <p>
              <FormattedMessage
                id='ui.PaymentModeModal.card'
                defaultMessage='Payer par carte bancaire'
              />
            </p>
          </Button>
        )}

        <Button
          onClick={() => {
            createUserLog({
              type: LogTypeEnum.CLICK_ON_PAYMENT_DEPOSIT_METHOD,
              clientRequestId,
              houseId,
              serviceProviderId,
              messages: ['Paiement par virement bancaire'],
            })

            window.open(
              'https://naboo-documents.s3.eu-west-3.amazonaws.com/Relev%C3%A9%20d%27identit%C3%A9%20bancaire%20WOME%20STAY%20SAS%20%28naboo%29%20certifi%C3%A9%20conforme.pdf',
              '_blank',
            )
            setIsPaymentModeModalOpen(false)
          }}
          className={s.paymentModeButton}
          color={BUTTON_COLOR.WHITE}
          fullWidth
        >
          <IconSvg.Bank style={{ alignSelf: 'center', marginRight: '20px' }} />
          <p>
            <FormattedMessage
              id='ui.PaymentModeModal.bankTransfer'
              defaultMessage='Payer par virement bancaire'
            />
          </p>
        </Button>
      </div>
    </Modal>
  )
}

export default PaymentModeModal
